import React from 'react';
import { Clock, User, FileText, Mail, Send, CheckCircle, XCircle, AlertCircle, Upload } from 'lucide-react';

const ActivityHistoryTab = ({ activities }) => {
  const getActivityIcon = (type) => {
    switch (type) {
      case 'submission':
        return <Send className="w-5 h-5 text-blue-600" />;
      case 'review':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'communication':
        return <Mail className="w-5 h-5 text-purple-600" />;
      case 'document_upload':
        return <Upload className="w-5 h-5 text-indigo-600" />;
      case 'status_change':
        return <AlertCircle className="w-5 h-5 text-orange-600" />;
      case 'rejection':
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return <FileText className="w-5 h-5 text-gray-600" />;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'submission':
        return 'bg-blue-100 border-blue-200';
      case 'review':
        return 'bg-green-100 border-green-200';
      case 'communication':
        return 'bg-purple-100 border-purple-200';
      case 'document_upload':
        return 'bg-indigo-100 border-indigo-200';
      case 'status_change':
        return 'bg-orange-100 border-orange-200';
      case 'rejection':
        return 'bg-red-100 border-red-200';
      default:
        return 'bg-gray-100 border-gray-200';
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
    } else if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} ${days === 1 ? 'day' : 'days'} ago`;
    } else {
      return date?.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      submission: 'Submission',
      review: 'Review',
      communication: 'Communication',
      document_upload: 'Document Upload',
      status_change: 'Status Change',
      rejection: 'Rejection'
    };
    return labels?.[type] || type?.replace('_', ' ')?.toUpperCase();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-gray-900">
          Activity Timeline ({activities?.length || 0} Activities)
        </h3>
      </div>
      {/* Timeline */}
      <div className="relative">
        {/* Timeline Line */}
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200" />

        {/* Activities */}
        <div className="space-y-6">
          {activities?.map((activity, index) => (
            <div key={activity?.id} className="relative pl-16">
              {/* Timeline Dot */}
              <div className={`absolute left-0 w-12 h-12 rounded-full border-2 flex items-center justify-center ${getActivityColor(activity?.type)}`}>
                {getActivityIcon(activity?.type)}
              </div>

              {/* Activity Card */}
              <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs font-medium rounded">
                        {getTypeLabel(activity?.type)}
                      </span>
                      <span className="text-sm text-gray-500">
                        {formatTimestamp(activity?.timestamp)}
                      </span>
                    </div>
                    <h4 className="text-base font-semibold text-gray-900 mb-1">
                      {activity?.description}
                    </h4>
                    {activity?.details && (
                      <p className="text-sm text-gray-600">
                        {activity?.details}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 text-sm text-gray-500 mt-3 pt-3 border-t border-gray-100">
                  <User className="w-4 h-4" />
                  <span className="font-medium">{activity?.user}</span>
                  <span>•</span>
                  <Clock className="w-4 h-4" />
                  <span>
                    {new Date(activity?.timestamp)?.toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Empty State */}
      {(!activities || activities?.length === 0) && (
        <div className="text-center py-12">
          <Clock className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Activities Yet</h3>
          <p className="text-gray-600">
            Activity history will appear here as actions are performed on this registration.
          </p>
        </div>
      )}
    </div>
  );
};

export default ActivityHistoryTab;